import { useCallback, useMemo, useState } from 'react';
import { useAvContext } from '@context/AvContextProvider';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { generateUUID } from '@utils/utils';
import { useSnackbar } from 'notistack';
import { PAGE_PATHS, Permission } from '../../../types';
import { FILE_CONCURRENCY, FreaPost, runWithConcurrency, uploadFileMultipart } from './multipartUpload';
import {
  CreateFreaSourceInput,
  FreaArtifactHistoryEntry,
  FreaBucketStatus,
  FreaFile,
  FreaListFilesResponse,
  FreaListSourcesResponse,
  FreaSource,
  FreaSourcesPagination,
  FreaWeeklyQuota,
  FreaWeeklyTokenStats,
  UploadingFile,
} from './types';
import { estimateSecondsLeft, MAX_FILES_PER_SESSION, validateFileForUpload } from './utils';

// frontier-api proxies Frontier's scanner-api; through the gateway this is {API_URL}/api/frontier-api/frea/*.
const FREA_API = 'frontier-api/frea';
const BUCKET_POLL_INTERVAL_MS = 15_000;
const MAX_FILE_PAGES = 20;

export const freaQueryKeys = {
  all: ['frea'] as const,
  bucket: ['frea', 'bucket'] as const,
  sources: ['frea', 'sources'] as const,
  sourcesPage: ({ page, rowsPerPage }: FreaSourcesPagination) => ['frea', 'sources', page, rowsPerPage] as const,
  files: (sourceId?: string) => ['frea', 'files', sourceId] as const,
  history: (sourceId?: string, artifactName?: string) => ['frea', 'history', sourceId, artifactName] as const,
  quota: ['frea', 'quota'] as const,
  stats: ['frea', 'stats'] as const,
};

/** Mirrors frontier-api: create/update calls require FRONTIER_API:UPDATE. */
export const useCanWriteFrea = () => {
  const { userPermissions } = useAvContext();
  return !!userPermissions.hasAllowedPermission({ path: PAGE_PATHS.FREA_SETTINGS, permission: Permission.UPDATE });
};

export const getFreaErrorMessage = (error: unknown, fallback: string) =>
  (error as { error?: string } | undefined)?.error || (error as Error | undefined)?.message || fallback;

const useFreaApi = () => {
  const { api } = useAvContext();
  return useMemo(() => {
    const request = <T>(path: string, method: string, body?: unknown, signal?: AbortSignal): Promise<T> =>
      api(`${FREA_API}/${path}`, {
        options: { method },
        ...(body === undefined ? {} : { body }),
        isWebserver: false,
        muteErrors: true,
        signal,
      });
    const post: FreaPost = <T>(path: string, body?: unknown) => request<T>(path, 'POST', body ?? {});
    return {
      get: <T>(path: string, signal?: AbortSignal) => request<T>(path, 'GET', undefined, signal),
      post,
      put: <T>(path: string, body: unknown) => request<T>(path, 'PUT', body),
    };
  }, [api]);
};

const query = (params: Record<string, string | number | undefined | null>) =>
  new URLSearchParams(
    Object.entries(params)
      .filter(([, value]) => value != null && value !== '')
      .map(([key, value]) => [key, String(value)])
  ).toString();

// No client-side caching of Frontier data (gcTime: 0), matching the EASM settings screen.
export const useGetFreaBucketStatus = () => {
  const { get } = useFreaApi();
  return useQuery<FreaBucketStatus>({
    queryKey: freaQueryKeys.bucket,
    queryFn: ({ signal }) => get<FreaBucketStatus>('bucket/status', signal),
    gcTime: 0,
    retry: false,
    refetchInterval: ({ state }) => (state.data?.status === 'DONE' ? false : BUCKET_POLL_INTERVAL_MS),
  });
};

export const useIsFreaProvisioned = (enabled: boolean) => {
  const { get } = useFreaApi();
  const { data, isError } = useQuery<FreaBucketStatus>({
    queryKey: freaQueryKeys.bucket,
    queryFn: ({ signal }) => get<FreaBucketStatus>('bucket/status', signal),
    enabled,
    retry: false,
  });
  return !isError && data?.status === 'DONE';
};

/** One server-side page of sources; ordering (most recently updated first) is done by scanner-api. */
export const useGetFreaSources = (pagination: FreaSourcesPagination, enabled = true) => {
  const { get } = useFreaApi();
  const { page, rowsPerPage } = pagination;
  return useQuery<FreaListSourcesResponse>({
    queryKey: freaQueryKeys.sourcesPage(pagination),
    queryFn: ({ signal }) =>
      get<FreaListSourcesResponse>(`sources?${query({ limit: rowsPerPage, offset: (page - 1) * rowsPerPage })}`, signal).then(response => ({
        sources: response?.sources ?? [],
        total: response?.total ?? 0,
      })),
    gcTime: 0,
    enabled,
    placeholderData: keepPreviousData,
  });
};

export const useGetFreaFiles = (sourceId?: string) => {
  const { get } = useFreaApi();
  return useQuery<FreaFile[]>({
    queryKey: freaQueryKeys.files(sourceId),
    queryFn: async ({ signal }) => {
      const collect = async (continuationToken: string | undefined, acc: FreaFile[], page: number): Promise<FreaFile[]> => {
        const response = await get<FreaListFilesResponse>(`files?${query({ sourceId, continuationToken })}`, signal);
        const files = [...acc, ...(response?.files ?? [])];
        return response?.continuationToken && page < MAX_FILE_PAGES ? collect(response.continuationToken, files, page + 1) : files;
      };
      return collect(undefined, [], 1);
    },
    enabled: !!sourceId,
    gcTime: 0,
  });
};

export const useGetFreaArtifactHistory = (sourceId?: string, artifactName?: string) => {
  const { get } = useFreaApi();
  return useQuery<FreaArtifactHistoryEntry[]>({
    queryKey: freaQueryKeys.history(sourceId, artifactName),
    queryFn: ({ signal }) =>
      get<{ history: FreaArtifactHistoryEntry[] }>(`files/history?${query({ sourceId, artifactName })}`, signal).then(
        response => response?.history ?? []
      ),
    enabled: !!sourceId && !!artifactName,
    gcTime: 0,
  });
};

export const useGetFreaWeeklyQuota = (enabled = true) => {
  const { get } = useFreaApi();
  return useQuery<FreaWeeklyQuota>({
    queryKey: freaQueryKeys.quota,
    queryFn: ({ signal }) => get<FreaWeeklyQuota>('quota/weekly', signal),
    enabled,
    gcTime: 0,
  });
};

export const useGetFreaWeeklyTokenStats = (enabled = true) => {
  const { get } = useFreaApi();
  return useQuery<FreaWeeklyTokenStats[]>({
    queryKey: freaQueryKeys.stats,
    queryFn: ({ signal }) => get<{ weeks: FreaWeeklyTokenStats[] }>('stats/tokens/weekly', signal).then(r => r?.weeks ?? []),
    enabled,
    gcTime: 0,
  });
};

export const useCreateFreaSource = () => {
  const { post } = useFreaApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFreaSourceInput) => post<FreaSource>('sources', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: freaQueryKeys.sources }),
  });
};

export const useUpdateFreaRetention = () => {
  const { put } = useFreaApi();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ sourceId, retentionDays }: { sourceId: string; retentionDays: number }) =>
      put<FreaSource>(`sources/${encodeURIComponent(sourceId)}/retention`, { retentionDays }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: freaQueryKeys.sources }),
    onError: error => enqueueSnackbar(getFreaErrorMessage(error, 'Failed to update retention'), { variant: 'error' }),
  });
};

/** AWS_S3 sources get an immediate Frontier re-sync; UPLOAD_FILE sources are just re-read. */
export const useRefreshFreaSource = () => {
  const { post } = useFreaApi();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [refreshingIds, setRefreshingIds] = useState<string[]>([]);

  const refresh = useCallback(
    async (source: FreaSource) => {
      setRefreshingIds(prev => (prev.includes(source.id) ? prev : [...prev, source.id]));
      try {
        if (source.sourceType === 'AWS_S3') {
          await post(`sources/${encodeURIComponent(source.id)}/refresh`);
        }
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: freaQueryKeys.sources }),
          queryClient.invalidateQueries({ queryKey: freaQueryKeys.files(source.id) }),
        ]);
      } catch (error) {
        enqueueSnackbar(getFreaErrorMessage(error, 'Failed to refresh'), { variant: 'error' });
      } finally {
        setRefreshingIds(prev => prev.filter(id => id !== source.id));
      }
    },
    [post, queryClient, enqueueSnackbar]
  );

  return { refresh, refreshingIds };
};

/**
 * Local upload queue for an UPLOAD_FILE source: validates selections, then uploads with multipart
 * (bounded per-file and per-part concurrency, per-part retry, abort on failure).
 */
export const useFreaFileUploads = () => {
  const { post } = useFreaApi();
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<UploadingFile[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const updateFile = (id: string, patch: Partial<UploadingFile>) =>
    setFiles(prev => prev.map(uploadingFile => (uploadingFile.id === id ? { ...uploadingFile, ...patch } : uploadingFile)));

  const addFiles = useCallback(
    (selected: File[]) => {
      const errors: string[] = [];
      const remainingSlots = MAX_FILES_PER_SESSION - files.length;
      if (selected.length > remainingSlots) {
        errors.push(`Cannot add ${selected.length} files — only ${remainingSlots} of ${MAX_FILES_PER_SESSION} slots remaining`);
      }
      const valid = selected.slice(0, Math.max(0, remainingSlots)).filter(file => {
        const result = validateFileForUpload(file);
        errors.push(...result.errors);
        return result.valid;
      });
      setValidationErrors(errors);
      const queued: UploadingFile[] = valid.map(file => ({ id: generateUUID(), file, progress: 0, status: 'QUEUED' as const }));
      if (queued.length) {
        setFiles(prev => [...prev, ...queued]);
      }
      return queued;
    },
    [files.length]
  );

  const removeFile = useCallback((id: string) => setFiles(prev => prev.filter(uploadingFile => uploadingFile.id !== id)), []);

  const clearDone = useCallback(() => setFiles(prev => prev.filter(uploadingFile => uploadingFile.status !== 'DONE')), []);

  /** Uploads every not-yet-done file; resolves with the number of files that failed. */
  const uploadAll = useCallback(
    async (sourceId: string, toUpload: UploadingFile[] = files) => {
      const pending = toUpload.filter(uploadingFile => uploadingFile.status !== 'DONE');
      const failedIds: string[] = [];
      setIsUploading(true);
      try {
        await runWithConcurrency(pending, FILE_CONCURRENCY, async uploadingFile => {
          updateFile(uploadingFile.id, { status: 'UPLOADING', progress: 0, secondsLeft: undefined });
          const startedAt = Date.now();
          try {
            await uploadFileMultipart({
              file: uploadingFile.file,
              sourceId,
              post,
              onProgress: progress =>
                updateFile(uploadingFile.id, { progress, secondsLeft: estimateSecondsLeft(Date.now() - startedAt, progress) }),
            });
            updateFile(uploadingFile.id, { status: 'DONE', progress: 100, secondsLeft: undefined });
          } catch {
            failedIds.push(uploadingFile.id);
            updateFile(uploadingFile.id, { status: 'ERROR', secondsLeft: undefined });
          }
        });
      } finally {
        setIsUploading(false);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: freaQueryKeys.sources }),
          queryClient.invalidateQueries({ queryKey: freaQueryKeys.files(sourceId) }),
        ]);
      }
      return failedIds.length;
    },
    [files, post, queryClient]
  );

  return { files, addFiles, removeFile, clearDone, uploadAll, isUploading, validationErrors };
};
